// src/pages/settings/ClientForm.tsx — formulaire de création/édition d'un client

import { useState, useEffect } from "react";
import type { ClientRecord } from "../../api/settingsApi";
import { uploadImage } from "../../api/uploadApi";
import { b64Bytes, fmtBytes, toCamelCase, toMaxH } from "./helpers";
import { ImageDropInput } from "./ImageDropInput";

export function ClientForm({ initial, onSave, onClose }:{
  initial?: ClientRecord; onSave:(c:ClientRecord)=>Promise<void>; onClose:()=>void;
}) {
  const [name,       setName]       = useState(initial?.name ?? "");
  const [logo,       setLogo]       = useState(initial?.logoBase64 ?? "");
  const [saving,     setSaving]     = useState(false);
  const [err,        setErr]        = useState<string|null>(null);
  const [logoInfo,   setLogoInfo]   = useState<{
    origW:number; origH:number; origBytes:number;
    convW:number; convH:number; convBytes:number;
  }|null>(null);

  useEffect(() => {
    if (!logo.startsWith("data:")) { setLogoInfo(null); return; }
    let active = true;
    (async () => {
      const origB64   = logo.split(",")[1] ?? "";
      const origBytes = b64Bytes(origB64);
      if (logo.startsWith("data:image/svg")) {
        if (active) setLogoInfo({ origW:0, origH:0, origBytes, convW:0, convH:0, convBytes:origBytes });
        return;
      }
      const [origW, origH] = await new Promise<[number,number]>(res => {
        const img = new Image(); img.onload = () => res([img.naturalWidth, img.naturalHeight]); img.src = logo;
      });
      const conv      = await toMaxH(logo, 512);
      const convBytes = b64Bytes(conv.split(",")[1] ?? "");
      const convH     = Math.min(origH, 512);
      const convW     = origH > 512 ? Math.round(origW * 512 / origH) : origW;
      if (active) setLogoInfo({ origW, origH, origBytes, convW, convH, convBytes });
    })();
    return () => { active = false; };
  }, [logo]);

  const derivedId = initial?.id ?? toCamelCase(name);

  const handleSave = async () => {
    if(!name.trim()){ setErr("Le nom est obligatoire."); return; }
    setSaving(true);
    try {
      const logoUrl = logo.startsWith("data:") ? await uploadImage(await toMaxH(logo, 512)) : logo;
      await onSave({ id:derivedId, name:name.trim(), logoBase64:logoUrl, createdAt:"", updatedAt:"" });
      onClose();
    } catch(e){ setErr((e as Error).message); setSaving(false); }
  };

  return (
    <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:0}}>
      {/* Scrollable content */}
      <div style={{overflowY:"auto",padding:"20px 24px",flex:1}}>
        <div className="mb-16">
          <label className="label">Nom du client *</label>
          <input value={name} onChange={e=>{setName(e.target.value);setErr(null);}} className="field" placeholder="ex : Ville de Lyon"/>
        </div>
        <div className="mb-16" style={{padding:"8px 12px",background:"#f8f8f8",borderRadius:6,fontSize:12,color:"#888", display: "none"}}>
          Identifiant généré : <code style={{fontWeight:700,color:"#555"}}>{derivedId||"…"}</code>
        </div>
        <div className="mb-4">
          <ImageDropInput label="Logo (optionnel)" value={logo} onChange={setLogo} height={80}/>
          <div style={{height:34,fontSize:10,color:"#aaa",lineHeight:1.7,textAlign:"right",marginTop:2}}>
            {logoInfo && (logo.startsWith("data:image/svg")
              ? <div>SVG · {fmtBytes(logoInfo.origBytes)}</div>
              : <>
                  {(logoInfo.origW !== logoInfo.convW || logoInfo.origH !== logoInfo.convH) && (
                    <div style={{textDecoration:"line-through",color:"#bbb"}}>
                      {logoInfo.origW} × {logoInfo.origH} px · {fmtBytes(logoInfo.origBytes)}
                    </div>
                  )}
                  <div>{logoInfo.convW} × {logoInfo.convH} px · {fmtBytes(logoInfo.convBytes)}</div>
                </>
            )}
          </div>
        </div>
      </div>
      {/* Sticky footer */}
      <div style={{padding:"16px 24px",borderTop:"1px solid #f0f0f0",background:"#fff",flexShrink:0,display:"flex",flexDirection:"column",gap:10}}>
        {err && <div style={{background:"#fdecea",border:"1px solid #f5c6cb",borderRadius:6,padding:"10px 14px",fontSize:13,color:"var(--red)"}}>⚠ {err}</div>}
        <div className="flex gap-10" style={{justifyContent:"flex-end"}}>
          <button onClick={onClose} className="btn btn-ghost">Annuler</button>
          <button onClick={handleSave} disabled={saving} className="btn btn-black btn-lg">
            {saving?"Enregistrement…":"Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
